import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import api, { tokenStore } from '@/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const TYCHI_SSO_URL = process.env.REACT_APP_TYCHI_SSO_URL || '';

const AuthContext = createContext(null);

let silentSsoTried = false;
// True only when this browser proved it holds a Master portal session
// (silent SSO succeeded). Gates sso-token polling so local-only accounts
// are never logged out by a missing Master cookie.
let masterLinked = false;

/** Hard redirect to our own login — never fires while already on /login. */
function redirectToLogin() {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
}

// Trim a response body to just the user-shaped fields we surface to the app.
function userFromResponse(data) {
  if (!data) return null;
  return {
    id: data.id || data._id,
    email: data.email,
    name: data.name,
    role: data.role,
    radar_role: data.radar_role,
    master_role: data.master_role,
    org_id: data.org_id,
    is_active: data.is_active,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // True while the one-shot silent Master SSO check is in flight on this tab.
  // Starts true only when SSO is configured, so the login form never flashes
  // before the check resolves. Empty REACT_APP_TYCHI_SSO_URL → stays false.
  const [ssoChecking, setSsoChecking] = useState(() => Boolean(TYCHI_SSO_URL));
  // True when the silent check found a VALID Master portal session (sso-token
  // returned 200 with an access_token), regardless of whether the Radar
  // exchange succeeded. Used by /login to auto-trigger the SSO button flow.
  const [masterSessionAvailable, setMasterSessionAvailable] = useState(false);
  const refreshing = useRef(null);

  /** Persist the bearer + user, then return the user. */
  const adoptSession = useCallback((data) => {
    if (data?.access_token) tokenStore.set(data.access_token);
    const u = userFromResponse(data);
    if (u && u.email) setUser(u);
    return u;
  }, []);

  /** Probe /auth/me using the in-memory bearer (or cookie). */
  const fetchMe = useCallback(async () => {
    const { data } = await api.get('/auth/me');
    const u = userFromResponse(data);
    setUser(u);
    return u;
  }, []);

  /**
   * Silent Master SSO — runs ONCE per browser tab, 4.5s timeout, no retries.
   *
   *  1. GET /api/auth/portal-sso-token on Radar (server forwards the shared
   *     tychi_refresh_token cookie to Master — works even when third-party
   *     cookies are blocked).
   *  2. If that fails, GET the Master sso-token URL directly with credentials.
   *  3. Never save the Master token as Radar's session: exchange it via
   *     POST /api/auth/portal-sso-exchange for a Radar-minted JWT (only
   *     succeeds for users that already exist in Radar).
   *
   * Any failure (401, network, timeout, unknown user) → resolve false and
   * stay silent. No redirect, no retry, no error banner.
   */
  const trySilentSso = useCallback(async () => {
    if (silentSsoTried || !TYCHI_SSO_URL) return false;
    silentSsoTried = true;
    try {
      let data = null;
      // Step 1 — Radar proxy of the Master sso-token endpoint
      try {
        const r = await api.get('/auth/portal-sso-token', {
          timeout: 4500,
          validateStatus: () => true,
        });
        if (r && r.status === 200) data = r.data;
      } catch { /* fall through to the direct call */ }
      // Step 2 — direct browser call to Master (shared .tychi.co cookie)
      if (!data) {
        const r = await axios.get(TYCHI_SSO_URL, {
          withCredentials: true,
          timeout: 4500,
          validateStatus: () => true,
        });
        if (r && r.status === 200) data = r.data;
      }
      // Missing or expired Master token → treat as a failed silent check
      const masterToken = data?.access_token;
      if (!masterToken) return false;
      // A valid Master session exists on this browser (even if the user
      // turns out not to be a Radar user) — /login uses this signal.
      setMasterSessionAvailable(true);
      // Step 3 — exchange for a Radar-native session
      const x = await api.post(
        '/auth/portal-sso-exchange',
        { access_token: masterToken },
        { timeout: 4500, validateStatus: () => true },
      );
      if (x && x.status === 200 && x.data?.access_token) {
        const u = adoptSession(x.data);
        if (u) {
          masterLinked = true;
          return true;
        }
      }
    } catch { /* silent — any failure shows the normal login page */ }
    return false;
  }, [adoptSession]);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      // No in-memory token yet on a fresh page load → /me will only succeed
      // if a same-origin httpOnly cookie is present. That's fine for legacy
      // logged-in users; for SSO path we go through trySilentSso first.
      await fetchMe();
      setSsoChecking(false);
    } catch {
      if (silentSsoTried || !TYCHI_SSO_URL) {
        setSsoChecking(false);
        setUser(false);
      } else {
        setSsoChecking(true);
        const ok = await trySilentSso();
        setSsoChecking(false);
        if (!ok) setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchMe, trySilentSso]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // 401 handling: SESSION_REVOKED → straight to login (SLO, no refresh, no
  // toast); any other 401 → refresh → retry (single in-flight refresh).
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (r) => r,
      async (err) => {
        const original = err.config || {};
        const status = err.response?.status;
        const url = (original.url || '').toString();
        const code = err.response?.data?.code || err.response?.data?.detail?.code;
        if (status === 401 && code === 'SESSION_REVOKED') {
          tokenStore.clear();
          setUser(false);
          redirectToLogin();
          return Promise.reject(err);
        }
        if (
          status === 401 &&
          !original._retry &&
          !url.includes('/auth/login') &&
          !url.includes('/auth/refresh') &&
          !url.includes('/auth/me')
        ) {
          original._retry = true;
          try {
            refreshing.current = refreshing.current || api.post('/auth/refresh');
            const r = await refreshing.current;
            refreshing.current = null;
            if (r?.data?.access_token) tokenStore.set(r.data.access_token);
            return api(original);
          } catch {
            refreshing.current = null;
            tokenStore.clear();
            setUser(false);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  // Session watchdog — authenticated pages only (never runs on /login).
  // Every 15s: probe our own session (a SESSION_REVOKED 401 is caught by the
  // interceptor above → hard redirect). For Master-linked sessions, also probe
  // the Master sso-token: 401 means Master signed out → clear the local
  // session server-side (so /login can't bounce back) and hard redirect.
  // Network errors are ignored — they are NOT logout.
  useEffect(() => {
    if (!user) return;
    const tick = async () => {
      try { await api.get('/auth/me'); } catch { /* interceptor handles SESSION_REVOKED */ }
      if (masterLinked && TYCHI_SSO_URL) {
        try {
          const r = await axios.get(TYCHI_SSO_URL, {
            withCredentials: true,
            timeout: 4000,
            validateStatus: () => true,
          });
          if (r && r.status === 401) {
            masterLinked = false;
            try { await api.post('/auth/logout'); } catch { /* best effort */ }
            tokenStore.clear();
            setUser(false);
            redirectToLogin();
          }
        } catch { /* network error — not logout */ }
      }
    };
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [user]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    return adoptSession(data);
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    return adoptSession(data);
  };

  const logout = async () => {
    masterLinked = false;
    try {
      await api.post('/auth/logout');
    } catch {}
    tokenStore.clear();
    setUser(false);
  };

  /** Called by /callback page after Cognito code-exchange returns the session.
   *  `data` is the response body from POST /api/auth/callback. */
  const adoptCallbackSession = useCallback((data) => adoptSession(data), [adoptSession]);

  // Kept for backwards-compat with code that still calls refreshUser()
  const refreshUser = useCallback(async () => {
    try { return await fetchMe(); } catch { setUser(false); return null; }
  }, [fetchMe]);

  return (
    <AuthContext.Provider value={{ user, loading, ssoChecking, masterSessionAvailable, login, register, logout, refreshUser, adoptCallbackSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
